import { type ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, CheckCircle2 } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Brand colours (eventy)                                             */
/*  Navy #252854 · Teal #2DD4BF · Violet #8B5CF6                      */
/* ------------------------------------------------------------------ */

interface SharedProps {
  flash: { alert?: string; notice?: string }
  [key: string]: unknown
}

interface AuthLayoutProps {
  locale: string
  logoUrl: string
  brandingTitle?: string
  brandingSubtitle?: string
  children: ReactNode
}

export default function AuthLayout({
  locale,
  logoUrl,
  brandingTitle,
  brandingSubtitle,
  children,
}: AuthLayoutProps) {
  const { flash } = usePage<SharedProps>().props
  const isRtl = locale === 'ar'

  return (
    <div
      className="min-h-screen flex flex-col md:flex-row"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {/* ─── Branding panel ─── */}
      <div className="relative hidden md:flex md:w-[420px] lg:w-[480px] shrink-0 flex-col items-center justify-center overflow-hidden">
        {/* Navy base with subtle teal→violet mesh */}
        <div className="absolute inset-0 bg-[#252854]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(45,212,191,.15),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,.18),transparent_50%)]" />

        {/* Decorative shapes — teal + violet glow rings */}
        <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full border border-[#2DD4BF]/10 bg-[#2DD4BF]/[0.04]" />
        <div className="absolute -bottom-28 -right-28 h-80 w-80 rounded-full border border-[#8B5CF6]/10 bg-[#8B5CF6]/[0.04]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center px-10 text-center">
          <img
            src={logoUrl}
            alt="Eventy"
            className="mb-10 w-52 drop-shadow-2xl animate-[float_4s_ease-in-out_infinite]"
          />
          {brandingTitle && (
            <h2 className="text-3xl font-light tracking-wide text-white mb-3">
              {brandingTitle}
            </h2>
          )}
          {brandingSubtitle && (
            <p className="text-base text-white/50 font-light max-w-[280px] leading-relaxed">
              {brandingSubtitle}
            </p>
          )}
        </div>
      </div>

      {/* ─── Form panel ─── */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header strip */}
        <div className="md:hidden bg-[#252854] px-6 py-7 text-center">
          <img src={logoUrl} alt="Eventy" className="mx-auto h-9 drop-shadow-lg" />
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center bg-slate-50/60 px-4 py-8 sm:px-8">
          <div className="w-full max-w-[440px] animate-[fadeSlideUp_0.4s_ease-out]">
            {/* Flash messages */}
            {flash?.alert && (
              <Alert
                variant="destructive"
                className="mb-5 rounded-xl border-red-200 bg-red-50/80 shadow-sm"
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{flash.alert}</AlertDescription>
              </Alert>
            )}
            {flash?.notice && (
              <Alert className="mb-5 rounded-xl border-teal-200 bg-teal-50/80 text-teal-800 shadow-sm">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{flash.notice}</AlertDescription>
              </Alert>
            )}

            {children}
          </div>
        </div>
      </div>

      {/* Shared keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
